import React, { useEffect, useRef, useState } from 'react';
import type { FuLocale } from '../../i18n/fuLocale';
import { AvatarImageError, prepareCreatureAvatar } from './prepareCreatureAvatar';

const text = {
  zh: {
    upload: '上传头像', replace: '更换头像', remove: '移除头像', busy: '正在处理头像…',
    type: '请选择 JPG 或 PNG 图片。', size: '图片过大，请选择 10 MB 以内的图片。', decode: '无法读取图片，请选择有效的 JPG 或 PNG。',
    hint: 'JPG / PNG · 自动压缩 · 房间内同步',
  },
  en: {
    upload: 'Upload avatar', replace: 'Replace avatar', remove: 'Remove avatar', busy: 'Processing avatar…',
    type: 'Choose a JPG or PNG image.', size: 'Image is too large. Choose a file under 10 MB.', decode: 'Unable to read this image. Choose a valid JPG or PNG.',
    hint: 'JPG / PNG · Compressed automatically · Shared in this room',
  },
};

export function CreatureAvatar({ name, avatar, canEdit, onChange, locale, identity }: {
  name: string;
  avatar?: string;
  canEdit: boolean;
  onChange: (avatar: string) => void;
  locale: FuLocale;
  identity?: object;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const generation = useRef({ value: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const t = text[locale];
  const action = `${avatar ? t.replace : t.upload} ${name}`;

  useEffect(() => {
    const requestState = generation.current;
    requestState.value++;
    setBusy(false);
    setError('');
    return () => { requestState.value++; };
  }, [name, canEdit, identity]);

  return (
    <div className="creature-avatar-control" aria-busy={busy}>
      <button type="button" className={`creature-avatar${avatar ? ' has-avatar' : ''}`}
        disabled={!canEdit || busy} aria-label={action} title={busy ? t.busy : `${action}\n${t.hint}`}
        onClick={() => inputRef.current?.click()}>
        {avatar ? <img src={avatar} alt={name} /> : <span aria-hidden="true">{Array.from(name.trim()).slice(0, 1).join('') || '＋'}</span>}
        {canEdit && <span className="creature-avatar-edit" aria-hidden="true">{busy ? '…' : '＋'}</span>}
      </button>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,.jpg,.jpeg,.png" hidden aria-label={action}
        disabled={!canEdit || busy} onChange={async event => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file || !canEdit || busy) return;
          const request = ++generation.current.value;
          setError(''); setBusy(true);
          try {
            const result = await prepareCreatureAvatar(file);
            if (request === generation.current.value) onChange(result);
          } catch (cause) {
            if (request === generation.current.value) {
              setError(t[cause instanceof AvatarImageError ? cause.reason : 'decode']);
            }
          } finally {
            if (request === generation.current.value) setBusy(false);
          }
        }} />
      {avatar && canEdit && <button type="button" className="creature-avatar-remove" disabled={busy}
        title={t.remove} aria-label={`${t.remove} ${name}`} onClick={() => { setError(''); onChange(''); }}>×</button>}
      {error && <span role="alert" className="creature-avatar-error">{error}</span>}
    </div>
  );
}
